/**
 * Email-first sign-in — one question at a time, both web portals (design
 * approved 26 Sep 2026).
 *
 *   email ──► password            (the account has one; "email me a code
 *        │                          instead" when Clerk also offers codes)
 *        └─► code                  (no password — a social or code-only
 *                                    account: a code goes out at once)
 *   password / code ──► signed in
 *                   └─► code (second factor)  Clerk's Client Trust: a new
 *                                               device must prove the email
 *                   └─► totp                   an authenticator app, where the
 *                                               portal allows it (`allowTotp`)
 *
 * Clerk's `signIn` resource carries the attempt; this hook only walks it. A
 * leftover session makes `create` refuse with "signed in" — the attempt signs
 * it out and starts once more (`retryAfterStaleSession`).
 *
 * Nothing throws and nothing is put into words here: each call resolves to an
 * outcome, and a failure carries Clerk's error (or a `SignInFlowError`) for
 * the screen's `useAuthErrorMessage`.
 */

import { useClerk, useSignIn } from '@clerk/clerk-react';
import { useCallback, useState } from 'react';

import { retryAfterStaleSession } from './stale-session';

type SignInResource = NonNullable<ReturnType<typeof useSignIn>['signIn']>;

export type SignInStep =
  | { step: 'email' }
  | { step: 'password'; email: string; canUseCode: boolean }
  | { step: 'code'; email: string; factor: 'first' | 'second' }
  | { step: 'totp'; email: string };

export type SignInOutcome =
  /** Moved to the next step. */
  | { outcome: 'next' }
  /** Signed in: the session is active. */
  | { outcome: 'complete' }
  | { outcome: 'failed'; error: unknown };

/** A dead end that is not Clerk refusing: Clerk asked for something this app does not offer. */
export class SignInFlowError extends Error {
  constructor(readonly code: string) {
    super(`Sign-in cannot continue: ${code}`);
    this.name = 'SignInFlowError';
  }
}

const fail = (error: unknown): SignInOutcome => ({ outcome: 'failed', error });

function emailFactorId(factors: SignInResource['supportedFirstFactors']): string | null {
  const factor = factors?.find((f) => f.strategy === 'email_code');
  return factor && 'emailAddressId' in factor ? factor.emailAddressId : null;
}

export function useEmailFirstSignIn({ allowTotp }: { allowTotp: boolean }) {
  const { signIn, isLoaded, setActive } = useSignIn();
  const clerk = useClerk();
  const [state, setState] = useState<SignInStep>({ step: 'email' });

  /** After a first factor: signed in, or a second factor to ask for. */
  const settle = useCallback(
    async (attempt: SignInResource, email: string): Promise<SignInOutcome> => {
      if (attempt.status === 'complete') {
        await setActive?.({ session: attempt.createdSessionId });
        return { outcome: 'complete' };
      }
      if (attempt.status === 'needs_second_factor' && signIn) {
        const factors = attempt.supportedSecondFactors ?? [];
        const emailCode = factors.find((f) => f.strategy === 'email_code');
        if (emailCode && 'emailAddressId' in emailCode) {
          await signIn.prepareSecondFactor({
            strategy: 'email_code',
            emailAddressId: emailCode.emailAddressId,
          });
          setState({ step: 'code', email, factor: 'second' });
          return { outcome: 'next' };
        }
        if (allowTotp && factors.some((f) => f.strategy === 'totp')) {
          setState({ step: 'totp', email });
          return { outcome: 'next' };
        }
        return fail(new SignInFlowError('no_supported_second_factor'));
      }
      return fail(new SignInFlowError(`status_${attempt.status}`));
    },
    [signIn, setActive, allowTotp]
  );

  /** Step 1: who is signing in. */
  const identify = useCallback(
    async (email: string): Promise<SignInOutcome> => {
      if (!isLoaded || !signIn) return fail(new SignInFlowError('not_loaded'));
      try {
        const attempt = await retryAfterStaleSession(
          () => signIn.create({ identifier: email }),
          () => clerk.signOut()
        );
        const factors = attempt.supportedFirstFactors ?? [];
        const emailId = emailFactorId(factors);
        if (factors.some((f) => f.strategy === 'password')) {
          setState({ step: 'password', email, canUseCode: emailId !== null });
          return { outcome: 'next' };
        }
        if (emailId) {
          await signIn.prepareFirstFactor({ strategy: 'email_code', emailAddressId: emailId });
          setState({ step: 'code', email, factor: 'first' });
          return { outcome: 'next' };
        }
        return fail(new SignInFlowError('no_supported_first_factor'));
      } catch (error) {
        return fail(error);
      }
    },
    [isLoaded, signIn, clerk]
  );

  const submitPassword = useCallback(
    async (password: string): Promise<SignInOutcome> => {
      if (!signIn || state.step !== 'password') return fail(new SignInFlowError('not_on_password'));
      try {
        const attempt = await signIn.attemptFirstFactor({ strategy: 'password', password });
        return await settle(attempt, state.email);
      } catch (error) {
        return fail(error);
      }
    },
    [signIn, state, settle]
  );

  /** "Email me a sign-in code instead." */
  const sendCodeInstead = useCallback(async (): Promise<SignInOutcome> => {
    if (!signIn || state.step !== 'password') return fail(new SignInFlowError('not_on_password'));
    const emailId = emailFactorId(signIn.supportedFirstFactors);
    if (!emailId) return fail(new SignInFlowError('no_email_code'));
    try {
      await signIn.prepareFirstFactor({ strategy: 'email_code', emailAddressId: emailId });
      setState({ step: 'code', email: state.email, factor: 'first' });
      return { outcome: 'next' };
    } catch (error) {
      return fail(error);
    }
  }, [signIn, state]);

  const submitCode = useCallback(
    async (code: string): Promise<SignInOutcome> => {
      if (!signIn || (state.step !== 'code' && state.step !== 'totp')) {
        return fail(new SignInFlowError('not_on_code'));
      }
      try {
        if (state.step === 'code' && state.factor === 'first') {
          const attempt = await signIn.attemptFirstFactor({ strategy: 'email_code', code });
          return await settle(attempt, state.email);
        }
        const attempt = await signIn.attemptSecondFactor({
          strategy: state.step === 'totp' ? 'totp' : 'email_code',
          code,
        });
        if (attempt.status !== 'complete') {
          return fail(new SignInFlowError(`status_${attempt.status}`));
        }
        await setActive?.({ session: attempt.createdSessionId });
        return { outcome: 'complete' };
      } catch (error) {
        return fail(error);
      }
    },
    [signIn, state, settle, setActive]
  );

  /** A new code to the same address. */
  const resendCode = useCallback(async (): Promise<SignInOutcome> => {
    if (!signIn || state.step !== 'code') return fail(new SignInFlowError('not_on_code'));
    try {
      if (state.factor === 'first') {
        const emailId = emailFactorId(signIn.supportedFirstFactors);
        if (!emailId) return fail(new SignInFlowError('no_email_code'));
        await signIn.prepareFirstFactor({ strategy: 'email_code', emailAddressId: emailId });
      } else {
        const factor = signIn.supportedSecondFactors?.find((f) => f.strategy === 'email_code');
        if (!factor || !('emailAddressId' in factor)) {
          return fail(new SignInFlowError('no_email_code'));
        }
        await signIn.prepareSecondFactor({
          strategy: 'email_code',
          emailAddressId: factor.emailAddressId,
        });
      }
      return { outcome: 'next' };
    } catch (error) {
      return fail(error);
    }
  }, [signIn, state]);

  /** Back to the email — a different account, or a typo. */
  const restart = useCallback(() => setState({ step: 'email' }), []);

  return {
    state,
    ready: isLoaded && !!signIn,
    identify,
    submitPassword,
    sendCodeInstead,
    submitCode,
    resendCode,
    restart,
  };
}
